import { useState } from "react";
import { Plus } from "lucide-react";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import {
  useDeleteChecklistItem,
  useEscalateChecklistItem,
  useIntakeChecklist,
  useUpdateChecklistItemNotes,
  useUpsertChecklistItem,
} from "@/hooks/deals/useDealDetail";
import { useUpdateDealSubState } from "@/hooks/deals/useDeals";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  TrackedItemRow,
  INTAKE_STATE_SET,
  type StateSet,
} from "@/components/DealFlow/shared/TrackedItemRow";
import { AttachCounterpartyModal } from "../AttachCounterpartyModal";
import { AddIntakeChecklistItemModal } from "../AddIntakeChecklistItemModal";
import { AssignmentModal } from "../AssignmentModal";
import { ChangeCounterpartyConfirmModal } from "../ChangeCounterpartyConfirmModal";
import { CounterpartyCard } from "./CounterpartyCard";
import { BrokerFeeAuthorizationCard } from "@/components/deals/BrokerFeeAuthorizationCard";
import { BuyerConsultingFeeAuthorizationCard } from "@/components/deals/BuyerConsultingFeeAuthorizationCard";
import {
  DEAL_PERMISSIONS,
  type ChecklistItemStatus,
  type DealDetailDto,
  type IntakeChecklistItemDto,
} from "@/types/deals";

interface Props {
  deal: DealDetailDto;
}

const INTAKE_ESCALATION_PATH =
  "Escalation routes to the Loan Administrator. If the LA can't resolve it, it escalates to Admin.";

// Deal Pipeline spec Appendix A — the NotApplicable transition is offered
// for optional items only; required template items must be Completed (or
// escalated), never N/A.
// LOI interim fix — an item can still be required and reach NotApplicable
// when the backend has flagged it eligible (allowNotApplicableOverride),
// e.g. the LOI item on a Deal that isn't yet LOISigned / PASigned.
export function intakeStateSetFor(required: boolean, allowNotApplicableOverride: boolean): StateSet {
  if (!required || allowNotApplicableOverride) return INTAKE_STATE_SET;
  // Required template items must be Completed (or escalated), never N/A — so
  // the required variant drops NotApplicable from the transitions, and with
  // them from the post-escalation exits (`escalationExits`).
  return {
    states: INTAKE_STATE_SET.states,
    transitions: {
      NotStarted: ["InProgress", "Complete"],
      InProgress: ["Complete"],
      Complete: [],
      Escalated: [],
      NotApplicable: [],
    },
    reworkState: "NotStarted",
  };
}

export function DealIntakeTab({ deal }: Props) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const checklist = useIntakeChecklist(deal.id);
  const upsert = useUpsertChecklistItem(deal.id);
  const remove = useDeleteChecklistItem(deal.id);
  const escalate = useEscalateChecklistItem(deal.id);
  const notesMutation = useUpdateChecklistItemNotes(deal.id);
  const advance = useUpdateDealSubState(deal.id);

  const [attachRole, setAttachRole] = useState<"Borrower" | "Seller" | "Business" | null>(null);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [assignB1Open, setAssignB1Open] = useState(false);
  const [confirmChangeRole, setConfirmChangeRole] = useState<
    "Borrower" | "Seller" | "Business" | null
  >(null);
  const [changePicker, setChangePicker] = useState<{
    role: "Borrower" | "Seller" | "Business";
    reason: string;
  } | null>(null);

  const readOnly = deal.stage !== "Intake";
  const canEdit = hasPermission(DEAL_PERMISSIONS.Update) && !readOnly;
  const canAssign = hasPermission(DEAL_PERMISSIONS.Assign);
  const canEscalateIntake = hasPermission(DEAL_PERMISSIONS.EscalateIntakeItem);

  async function escalateItem(item: IntakeChecklistItemDto, reason: string) {
    try {
      await escalate.mutateAsync({ itemId: item.id, reason });
      toast.success(`"${item.title}" escalated.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function saveItemNotes(item: IntakeChecklistItemDto, notes: string) {
    try {
      await notesMutation.mutateAsync({ itemId: item.id, notes });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  // UAT 2026-05-15 — counterparties can be swapped at any pre-Closing stage
  // by a user holding DealFlow.ChangeCounterparty (Admin + Loan Admin).
  const hasChangeCounterpartyPermission = hasPermission(
    DEAL_PERMISSIONS.ChangeCounterparty,
  );
  const stageAllowsCounterpartyChange =
    deal.stage !== "Closing" &&
    deal.stage !== "Closed" &&
    deal.stage !== "Declined" &&
    deal.stage !== "Withdrawn";
  const canChangeCounterparty =
    hasChangeCounterpartyPermission && stageAllowsCounterpartyChange;
  const changeCounterpartyTooltip = !hasChangeCounterpartyPermission
    ? "You don't have permission to change counterparties."
    : !stageAllowsCounterpartyChange
      ? "Counterparties cannot be changed once the deal enters Closing."
      : undefined;

  const items = checklist.data?.items ?? [];
  const required = items.filter((i) => i.required);
  const requiredIncomplete = required.filter(
    (i) => i.status !== "Complete" && i.status !== "NotApplicable",
  );
  const allRequiredDone = requiredIncomplete.length === 0;

  async function setItemStatus(item: IntakeChecklistItemDto, status: ChecklistItemStatus) {
    try {
      await upsert.mutateAsync({
        itemId: item.id,
        body: { status, notes: item.notes, assignedTo: item.assignedTo },
      });
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Update failed.",
      );
    }
  }

  async function deleteItem(itemId: string) {
    try {
      await remove.mutateAsync(itemId);
      toast.success("Item removed.");
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Delete failed.",
      );
    }
  }

  async function advanceTo(
    target: "GatheringParties" | "IntakeReview" | "ReadyForAssignment",
  ) {
    try {
      await advance.mutateAsync({ targetSubState: target });
      toast.success(
        target === "GatheringParties"
          ? "Intake started."
          : target === "IntakeReview"
            ? "Intake review marked complete."
            : "Marked ready for assignment.",
      );
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : "Cannot advance.",
      );
    }
  }

  // Group items by category for readability.
  const grouped = items.reduce<Record<string, IntakeChecklistItemDto[]>>((acc, it) => {
    acc[it.category] ??= [];
    acc[it.category].push(it);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-space-4">
      <Card>
        <div className="flex items-center justify-between mb-space-3">
          <div>
            <div className="font-display font-bold text-h3 text-onyx">Counterparties</div>
            <p className="text-small text-gray-mid m-0">
              Attach the Borrower, Seller, and operating Business for this deal.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
          <CounterpartyAttachRow
            role="Borrower"
            attached={!!deal.borrowerPartyId}
            kindLabel={null}
            attachDisabled={!canEdit}
            onAttach={() => setAttachRole("Borrower")}
            changeDisabled={!canChangeCounterparty}
            changeTooltip={changeCounterpartyTooltip}
            onChange={() => setConfirmChangeRole("Borrower")}
            renderAttached={() =>
              deal.borrowerPartyId ? (
                <CounterpartyCard
                  role="Borrower"
                  kind="Party"
                  partyType="Borrower"
                  partyId={deal.borrowerPartyId}
                />
              ) : null
            }
          />

          <CounterpartyAttachRow
            role="Seller"
            attached={!!deal.sellerPartyId}
            kindLabel={null}
            attachDisabled={!canEdit}
            onAttach={() => setAttachRole("Seller")}
            changeDisabled={!canChangeCounterparty}
            changeTooltip={changeCounterpartyTooltip}
            onChange={() => setConfirmChangeRole("Seller")}
            renderAttached={() =>
              deal.sellerPartyId ? (
                <CounterpartyCard
                  role="Seller"
                  kind="Party"
                  partyType="Seller"
                  partyId={deal.sellerPartyId}
                />
              ) : null
            }
          />

          <CounterpartyAttachRow
            role="Business"
            attached={!!deal.businessId}
            kindLabel="Business"
            attachDisabled={!canEdit}
            onAttach={() => setAttachRole("Business")}
            changeDisabled={!canChangeCounterparty}
            changeTooltip={changeCounterpartyTooltip}
            onChange={() => setConfirmChangeRole("Business")}
            renderAttached={() =>
              deal.businessId ? (
                <CounterpartyCard role="Business" kind="Business" businessId={deal.businessId} />
              ) : null
            }
          />
        </div>
      </Card>

      <BrokerFeeAuthorizationCard deal={deal} />

      <BuyerConsultingFeeAuthorizationCard deal={deal} />

      <Card>
        <div className="flex items-center justify-between mb-space-3">
          <div>
            <div className="font-display font-bold text-h3 text-onyx">Intake checklist</div>
            <p className="text-small text-gray-mid m-0">
              {checklist.isLoading
                ? "Loading…"
                : `${required.length - requiredIncomplete.length} / ${required.length} required items complete.`}
            </p>
          </div>
          {canEdit && (
            <Button variant="secondary" size="sm" onClick={() => setAddItemOpen(true)}>
              <Plus size={14} aria-hidden /> Add custom item
            </Button>
          )}
        </div>

        {checklist.isLoading ? (
          <div className="text-small text-gray-mid italic">Loading checklist…</div>
        ) : items.length === 0 ? (
          <div className="text-small text-gray-mid italic">
            No checklist items yet. The standard template is provisioned when the
            deal enters Intake.
          </div>
        ) : (
          <div className="flex flex-col gap-space-4">
            {Object.entries(grouped).map(([category, list]) => (
              <div key={category}>
                <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
                  {category}
                </div>
                <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
                  {list.map((item) => (
                    <TrackedItemRow
                      key={item.id}
                      title={item.title}
                      description={item.description ?? undefined}
                      escalationPath={INTAKE_ESCALATION_PATH}
                      metadata={[
                        { label: "Category", value: item.category },
                        {
                          label: "Requirement",
                          value: item.required ? "Required" : "Optional",
                        },
                        {
                          label: "Source",
                          value: item.isStandardTemplateItem ? "Standard template" : "Custom",
                        },
                      ]}
                      detailExtra={
                        canEdit && !item.isStandardTemplateItem ? (
                          <Button
                            variant="tertiary"
                            size="sm"
                            disabled={remove.isPending}
                            onClick={() => deleteItem(item.id)}
                          >
                            Remove custom item
                          </Button>
                        ) : undefined
                      }
                      state={item.status}
                      stateSet={intakeStateSetFor(item.required, item.allowNotApplicableOverride)}
                      onStateChange={(next) =>
                        setItemStatus(item, next as ChecklistItemStatus)
                      }
                      stateChangePending={upsert.isPending}
                      notes={item.notes ?? ""}
                      onNotesChange={(notes) => saveItemNotes(item, notes)}
                      notesSavePending={notesMutation.isPending}
                      canEscalate={canEscalateIntake && canEdit}
                      escalationStatus={item.escalation?.status ?? "None"}
                      escalationDirective={item.escalation?.resolutionDirective}
                      escalationResolvedAt={item.escalation?.resolvedAt}
                      onEscalate={(reason) => escalateItem(item, reason)}
                      escalatePending={escalate.isPending}
                      isReadOnly={!canEdit}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </Card>

      {!readOnly && (
        <Card className="flex flex-wrap items-center justify-between gap-space-3">
          <div className="text-small text-gray-mid">
            {!allRequiredDone && requiredIncomplete.length > 0
              ? `${requiredIncomplete.length} required item${requiredIncomplete.length === 1 ? "" : "s"} still incomplete.`
              : "All required items are complete."}
          </div>
          <div className="flex items-center gap-space-2">
            {deal.subState === "New" && canEdit && (
              <Button
                variant="primary"
                onClick={() => advanceTo("GatheringParties")}
                disabled={advance.isPending}
              >
                Begin Intake
              </Button>
            )}
            {deal.subState === "GatheringParties" && canEdit && (
              <Button
                variant="primary"
                onClick={() => advanceTo("IntakeReview")}
                disabled={!allRequiredDone || advance.isPending}
                title={!allRequiredDone ? "Complete all required items first." : undefined}
              >
                Mark Review Complete
              </Button>
            )}
            {deal.subState === "IntakeReview" && canEdit && (
              <Button
                variant="primary"
                onClick={() => advanceTo("ReadyForAssignment")}
                disabled={advance.isPending}
              >
                Mark Ready for Assignment
              </Button>
            )}
            {deal.subState === "ReadyForAssignment" && canAssign && (
              <Button variant="primary" onClick={() => setAssignB1Open(true)}>
                Assign to B1
              </Button>
            )}
          </div>
        </Card>
      )}

      <AttachCounterpartyModal
        open={!!attachRole}
        deal={attachRole ? deal : null}
        role={attachRole ?? "Borrower"}
        onClose={() => setAttachRole(null)}
      />
      <ChangeCounterpartyConfirmModal
        open={confirmChangeRole !== null}
        partyType={confirmChangeRole ?? "Borrower"}
        onClose={() => setConfirmChangeRole(null)}
        onConfirm={(reason) => {
          if (confirmChangeRole) {
            setChangePicker({ role: confirmChangeRole, reason });
          }
          setConfirmChangeRole(null);
        }}
      />
      <AttachCounterpartyModal
        open={changePicker !== null}
        deal={changePicker ? deal : null}
        role={changePicker?.role ?? "Borrower"}
        changeReason={changePicker?.reason ?? null}
        onClose={() => setChangePicker(null)}
      />
      <AddIntakeChecklistItemModal
        open={addItemOpen}
        dealId={deal.id}
        onClose={() => setAddItemOpen(false)}
      />
      <AssignmentModal
        open={assignB1Open}
        deal={assignB1Open ? deal : null}
        role="B1"
        onClose={() => setAssignB1Open(false)}
      />
    </div>
  );
}

function CounterpartyAttachRow({
  role,
  attached,
  kindLabel,
  attachDisabled,
  onAttach,
  changeDisabled,
  changeTooltip,
  onChange,
  renderAttached,
}: {
  role: "Borrower" | "Seller" | "Business";
  attached: boolean;
  kindLabel: string | null;
  attachDisabled: boolean;
  onAttach: () => void;
  changeDisabled: boolean;
  changeTooltip?: string;
  onChange: () => void;
  renderAttached: () => React.ReactNode;
}) {
  return (
    <div className="border border-border-light rounded-md p-space-3 flex flex-col gap-space-2">
      <div className="flex items-center justify-between">
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          {role}
        </div>
        {attached && kindLabel && <Badge status="info">{kindLabel}</Badge>}
      </div>
      {attached ? (
        <>
          {renderAttached()}
          <Button
            variant="tertiary"
            size="sm"
            onClick={onChange}
            disabled={changeDisabled}
            title={changeDisabled ? changeTooltip : undefined}
            className="self-start"
          >
            Change
          </Button>
        </>
      ) : (
        <>
          <p className="text-small text-gray-mid italic m-0">No {role.toLowerCase()} attached.</p>
          <Button variant="primary" size="sm" onClick={onAttach} disabled={attachDisabled}>
            <Plus size={14} aria-hidden /> Attach {role.toLowerCase()}
          </Button>
        </>
      )}
    </div>
  );
}
